import Cookies from 'js-cookie';

// Configs
import ENV from '@shared/configs';

// Constants
import {
  API_ERROR_MESSAGE,
  STATUS_CODE,
  STATUS_MESSAGES,
} from '@shared/constants';
import {
  AUTH_ERROR_CODES,
  AUTH_ERROR_MESSAGES,
  COOKIE_KEYS,
} from '@/constants/auth';

// Types
import {
  ApiResponse,
  CommonError,
  ErrorType,
  HttpMethod,
  RequestConfig,
} from '@/types';

// Utils
import { sentryLogAPIFailed } from '@/utils/sentry';

const { API_ENDPOINT } = ENV;
const BASE_URL = API_ENDPOINT || '';

// Sleep utility function for delay between retries
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const httpClient = async <T>(
  endpoint: string,
  config: RequestConfig = {},
  isCustomBaseURL = false,
  isDirectResponse = false,
): Promise<ApiResponse<T>> => {
  const {
    method = HttpMethod.GET,
    headers = {},
    body,
    cache,
    revalidate,
    tags,
    retry,
    token,
    userEmail,
    isFullResponse = false,
  } = config;

  const retryConfig = {
    ...retry,
  };

  let attemptsLeft = retryConfig.attempts || 1;
  let lastError: unknown = null;
  let delayTime = retryConfig.delay || 0;
  const statusCodesToRetry = retryConfig.statusCodesToRetry || [
    STATUS_CODE.SERVICE_UNAVAILABLE,
  ];

  while (attemptsLeft > 0) {
    try {
      // Get user role from cookie client
      const userRole = Cookies.get(COOKIE_KEYS.USER_ROLE) || '';

      let requestHeaders: HeadersInit = {
        Authorization: `Bearer ${token}`,
        'x-user-role': userRole,
        'ngrok-skip-browser-warning': 'true',
        ...headers,
      };

      let requestBody: BodyInit | null = null;

      if (body instanceof FormData) {
        requestBody = body;
      } else if (body) {
        requestHeaders = {
          'Content-Type': 'application/json',
          ...requestHeaders,
        };
        requestBody = JSON.stringify(body);
      }

      const requestConfig: RequestInit = {
        method,
        headers: requestHeaders,
        body: requestBody,
        cache,
        next: {
          revalidate,
          tags,
        },
      };

      const response = await fetch(
        `${isCustomBaseURL ? '' : BASE_URL}${endpoint}`,
        requestConfig,
      );

      if (response.status === 204) {
        return {
          data: null,
          error: null,
          status: response.status,
        };
      }

      // If JSON parsing fails, return an empty object to avoid throwing an unexpected error
      const responseData = await response.json().catch(() => ({}));

      if (response.status === STATUS_CODE.UNAUTHORIZED) {
        throw new Error(STATUS_MESSAGES[STATUS_CODE.UNAUTHORIZED]);
      }

      // Handle 403 Forbidden responses
      if (response.status === STATUS_CODE.FORBIDDEN) {
        // Check for role mismatch error code
        if (responseData.code === AUTH_ERROR_CODES.ROLE_MISMATCH) {
          throw new Error(AUTH_ERROR_MESSAGES.ROLE_MISMATCH);
        }

        throw new Error(STATUS_MESSAGES[STATUS_CODE.FORBIDDEN]);
      }

      if (!response.ok) {
        if (statusCodesToRetry.includes(response.status) && attemptsLeft > 1) {
          // Always retry if we have attempts left
          attemptsLeft--;
          // Use exponential backoff for delays between retries
          await sleep(delayTime);
          delayTime *= 2; // Exponential backoff
          continue;
        }

        // No more retry attempts, return the error
        sentryLogAPIFailed({
          error: responseData,
          params: {
            endpoint,
            requestBody,
            status: response.status,
            user: {
              email: userEmail,
            },
          },
        });

        return {
          data: null,
          error:
            (responseData as CommonError)?.message ||
            STATUS_MESSAGES[response.status as STATUS_CODE] ||
            API_ERROR_MESSAGE.COMMON_ERROR,
          status: response.status,
        };
      }

      // Success! Return the response
      return {
        data:
          isFullResponse || isDirectResponse
            ? (responseData as T)
            : (responseData as { data: T }).data,
        error: null,
        status: response.status,
      };
    } catch (error: unknown) {
      // If this is an UNAUTHORIZED error that
      // we intentionally threw, re-throw it
      if (
        error instanceof Error &&
        (error.message === STATUS_MESSAGES[STATUS_CODE.UNAUTHORIZED] ||
          error.message === AUTH_ERROR_MESSAGES.ROLE_MISMATCH ||
          error.message === STATUS_MESSAGES[STATUS_CODE.FORBIDDEN])
      ) {
        throw error;
      }

      // Network errors or other exceptions
      lastError = error;

      if (attemptsLeft > 1) {
        attemptsLeft--;
        await sleep(delayTime);
        delayTime *= 2; // Exponential backoff
      } else {
        // Last attempt failed, return error
        sentryLogAPIFailed({
          error: lastError as ErrorType,
          params: {
            user: {
              email: userEmail,
            },
          },
        });

        return {
          data: null,
          error:
            error instanceof Error
              ? error.message
              : API_ERROR_MESSAGE.NETWORK_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        };
      }
    }
  }

  // This should never be reached, but TypeScript requires a return
  return {
    data: null,
    error:
      lastError instanceof Error
        ? lastError.message
        : API_ERROR_MESSAGE.MAXIMUM_RETRY_ATTEMPTS,
    status: STATUS_CODE.INTERNAL_SERVER_ERROR,
  };
};

export { httpClient };
